import React, { useState } from 'react';
import { ExerciseSelector } from './ExerciseSelector';
import { ExerciseOrderManager } from './ExerciseOrderManager';
import type { Exercise, ProgramExercise } from '../types';

interface ProgramCreatorProps {
  onCreateProgram: (programData: {
    name: string;
    workouts: Array<{
      name: string;
      exercises: Array<{
        exerciseId: string;
        targetSets?: number;
        targetRepsMin?: number;
        targetRepsMax?: number;
        restSeconds?: number;
      }>;
    }>;
    setAsActive: boolean;
  }) => Promise<void>;
  onCancel: () => void;
  isCreating: boolean;
}

interface WorkoutData {
  name: string;
  exercises: ProgramExercise[];
}

type Step = 'basic' | 'workouts' | 'exercises' | 'review';

/**
 * ProgramCreator - Multi-step form for creating training programs
 * Implements multi-step form with exercise selection from catalog
 * Requirements: 3.1, 3.2, 3.3, 3.4
 */
export const ProgramCreator: React.FC<ProgramCreatorProps> = ({
  onCreateProgram,
  onCancel,
  isCreating
}) => {
  const [currentStep, setCurrentStep] = useState<Step>('basic');
  const [programName, setProgramName] = useState('');
  const [setAsActive, setSetAsActive] = useState(true);
  const [workouts, setWorkouts] = useState<WorkoutData[]>([]);
  const [currentWorkoutIndex, setCurrentWorkoutIndex] = useState(0);
  const [showExerciseSelector, setShowExerciseSelector] = useState(false);

  // Step 1: Basic program info
  const renderBasicStep = () => (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold mb-4">Основная информация</h2>
      
      <div>
        <label htmlFor="program-name" className="block text-sm font-medium text-gray-700 mb-1">
          Название программы *
        </label>
        <input
          id="program-name"
          type="text"
          value={programName}
          onChange={(e) => setProgramName(e.target.value)}
          placeholder="Например: Силовая программа A/B"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          maxLength={100}
        />
      </div>

      <div className="flex items-center">
        <input
          id="set-active"
          type="checkbox"
          checked={setAsActive}
          onChange={(e) => setSetAsActive(e.target.checked)}
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
        <label htmlFor="set-active" className="ml-2 block text-sm text-gray-700">
          Сделать активной программой
        </label>
      </div>

      <div className="flex justify-between pt-4">
        <button
          onClick={onCancel}
          className="btn-secondary"
          disabled={isCreating}
        >
          Отмена
        </button>
        <button
          onClick={() => setCurrentStep('workouts')}
          disabled={!programName.trim() || isCreating}
          className="btn-primary"
        >
          Далее
        </button>
      </div>
    </div>
  );

  // Step 2: Configure workouts
  const renderWorkoutsStep = () => {
    const addWorkout = () => {
      const workoutName = String.fromCharCode(65 + workouts.length); // A, B, C, etc.
      setWorkouts([...workouts, { name: workoutName, exercises: [] }]);
    };

    const removeWorkout = (index: number) => {
      const newWorkouts = workouts.filter((_, i) => i !== index);
      setWorkouts(newWorkouts);
      if (currentWorkoutIndex >= newWorkouts.length) {
        setCurrentWorkoutIndex(Math.max(0, newWorkouts.length - 1));
      }
    };

    const updateWorkoutName = (index: number, name: string) => {
      const newWorkouts = [...workouts];
      newWorkouts[index].name = name;
      setWorkouts(newWorkouts);
    };

    return (
      <div className="space-y-4">
        <h2 className="text-xl font-semibold mb-4">Тренировки</h2>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-blue-800">
            Создайте тренировки для вашей программы (например: A, B, C для чередующихся тренировок)
          </p>
        </div>

        {/* Workout List */}
        <div className="space-y-3">
          {workouts.map((workout, index) => (
            <div key={index} className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg">
              <div className="flex-1">
                <input
                  type="text"
                  value={workout.name}
                  onChange={(e) => updateWorkoutName(index, e.target.value)}
                  placeholder="Название тренировки"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  maxLength={50}
                />
              </div>
              <div className="text-sm text-gray-500">
                {workout.exercises.length} упражнений
              </div>
              <button
                onClick={() => removeWorkout(index)}
                className="text-red-600 hover:text-red-800 p-1"
                title="Удалить тренировку"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          ))}
        </div>

        {/* Add Workout Button */}
        <button
          onClick={addWorkout}
          className="w-full py-2 px-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-800 transition-colors"
        >
          + Добавить тренировку
        </button>

        <div className="flex justify-between pt-4">
          <button
            onClick={() => setCurrentStep('basic')}
            className="btn-secondary"
            disabled={isCreating}
          >
            Назад
          </button>
          <button
            onClick={() => {
              if (workouts.length > 0) {
                setCurrentWorkoutIndex(0);
                setCurrentStep('exercises');
              }
            }}
            disabled={workouts.length === 0 || workouts.some(w => !w.name.trim()) || isCreating}
            className="btn-primary"
          >
            Далее
          </button>
        </div>
      </div>
    );
  };

  // Step 3: Add exercises to workouts
  const renderExercisesStep = () => {
    const currentWorkout = workouts[currentWorkoutIndex];
    
    const addExerciseToWorkout = (exercise: Exercise) => {
      const newWorkouts = [...workouts];
      const currentWorkoutExercises = newWorkouts[currentWorkoutIndex].exercises;
      
      // Check if exercise is already added to prevent duplicates
      const exerciseExists = currentWorkoutExercises.some(ex => ex.exercise.id === exercise.id);
      if (exerciseExists) {
        console.log('Exercise already exists in workout:', exercise.name_ru);
        return;
      }
      
      const newExercise: ProgramExercise = {
        id: `temp-${Date.now()}-${Math.random()}`, // Temporary ID for new exercises
        workout_id: '', // Will be set when saving
        order_index: currentWorkoutExercises.length,
        target_sets: 3,
        target_reps_min: 8,
        target_reps_max: 12,
        exercise
      };
      
      newWorkouts[currentWorkoutIndex].exercises.push(newExercise);
      setWorkouts(newWorkouts);
      setShowExerciseSelector(false);
    };

    const removeExerciseFromWorkout = (exerciseId: string) => {
      const newWorkouts = [...workouts];
      const exerciseIndex = newWorkouts[currentWorkoutIndex].exercises.findIndex(ex => ex.id === exerciseId);
      if (exerciseIndex !== -1) {
        newWorkouts[currentWorkoutIndex].exercises.splice(exerciseIndex, 1);
        // Update order indices
        newWorkouts[currentWorkoutIndex].exercises.forEach((ex, index) => {
          ex.order_index = index;
        });
        setWorkouts(newWorkouts);
      }
    };

    const updateExerciseTargets = (
      exerciseId: string,
      targetSets?: number,
      targetRepsMin?: number,
      targetRepsMax?: number,
      restSeconds?: number
    ) => {
      const newWorkouts = [...workouts];
      const exercise = newWorkouts[currentWorkoutIndex].exercises.find(ex => ex.id === exerciseId);
      if (exercise) {
        exercise.target_sets = targetSets;
        exercise.target_reps_min = targetRepsMin;
        exercise.target_reps_max = targetRepsMax;
        exercise.rest_seconds = restSeconds;
        setWorkouts(newWorkouts);
      }
    };

    const handleExerciseReorder = (reorderedExercises: ProgramExercise[]) => {
      const newWorkouts = [...workouts];
      newWorkouts[currentWorkoutIndex].exercises = reorderedExercises;
      setWorkouts(newWorkouts);
    };

    const goToNextWorkout = () => {
      if (currentWorkoutIndex < workouts.length - 1) {
        setCurrentWorkoutIndex(currentWorkoutIndex + 1);
      } else {
        setCurrentStep('review');
      }
    };

    const goToPreviousWorkout = () => {
      if (currentWorkoutIndex > 0) {
        setCurrentWorkoutIndex(currentWorkoutIndex - 1);
      } else {
        setCurrentStep('workouts');
      }
    };

    if (showExerciseSelector) {
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">
              Выберите упражнение для тренировки "{currentWorkout.name}"
            </h2>
            <button
              onClick={() => setShowExerciseSelector(false)}
              className="btn-secondary"
            >
              Отмена
            </button>
          </div>
          
          <ExerciseSelector
            onExerciseSelect={addExerciseToWorkout}
            showDetails={true}
          />
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">
            Упражнения для тренировки "{currentWorkout.name}"
          </h2>
          <div className="text-sm text-gray-500">
            {currentWorkoutIndex + 1} из {workouts.length}
          </div>
        </div>

        {/* Exercise List */}
        <ExerciseOrderManager
          exercises={currentWorkout.exercises}
          onReorder={handleExerciseReorder}
          onRemoveExercise={removeExerciseFromWorkout}
          onUpdateTargets={updateExerciseTargets}
          className="mb-4"
        />

        {/* Add Exercise Button */}
        <button
          onClick={() => setShowExerciseSelector(true)}
          className="w-full py-3 px-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-800 transition-colors"
        >
          + Добавить упражнение
        </button>

        {/* Navigation */}
        <div className="flex justify-between pt-4">
          <button
            onClick={goToPreviousWorkout}
            className="btn-secondary"
            disabled={isCreating}
          >
            {currentWorkoutIndex === 0 ? 'Назад' : 'Предыдущая тренировка'}
          </button>
          <button
            onClick={goToNextWorkout}
            className="btn-primary"
            disabled={isCreating}
          >
            {currentWorkoutIndex === workouts.length - 1 ? 'К обзору' : 'Следующая тренировка'}
          </button>
        </div>
      </div>
    );
  };

  // Step 4: Review and create
  const renderReviewStep = () => {
    const handleCreate = async () => {
      const programData = {
        name: programName,
        workouts: workouts.map(workout => ({
          name: workout.name,
          exercises: workout.exercises.map(exerciseData => {
            console.log('📝 Exercise data:', {
              tempId: exerciseData.id,
              realExerciseId: exerciseData.exercise.id,
              exerciseName: exerciseData.exercise.name_ru
            });
            
            return {
              exerciseId: exerciseData.exercise.id,
              targetSets: exerciseData.target_sets,
              targetRepsMin: exerciseData.target_reps_min,
              targetRepsMax: exerciseData.target_reps_max,
              restSeconds: exerciseData.rest_seconds
            };
          })
        })),
        setAsActive
      };

      console.log('📋 Program data to create:', JSON.stringify(programData, null, 2));
      await onCreateProgram(programData);
    };

    return (
      <div className="space-y-4">
        <h2 className="text-xl font-semibold mb-4">Обзор программы</h2>
        
        {/* Program Summary */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h3 className="font-medium mb-2">📋 {programName}</h3>
          <div className="text-sm text-gray-600 space-y-1">
            <p>Тренировок: {workouts.length}</p>
            <p>Всего упражнений: {workouts.reduce((total, workout) => total + workout.exercises.length, 0)}</p>
            {setAsActive && <p className="text-green-600 font-medium">✓ Будет установлена как активная</p>}
          </div>
        </div>

        {/* Workouts Overview */}
        <div className="space-y-3">
          {workouts.map((workout, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium mb-2">Тренировка {workout.name}</h4>
              <div className="space-y-2">
                {workout.exercises.map((exerciseData, exerciseIndex) => (
                  <div key={exerciseIndex} className="flex justify-between items-center text-sm">
                    <span>{exerciseData.exercise.name_ru}</span>
                    <span className="text-gray-500">
                      {exerciseData.target_sets} × {exerciseData.target_reps_min}-{exerciseData.target_reps_max}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-between pt-4">
          <button
            onClick={() => {
              setCurrentWorkoutIndex(workouts.length - 1);
              setCurrentStep('exercises');
            }}
            className="btn-secondary"
            disabled={isCreating}
          >
            Назад
          </button>
          <button
            onClick={handleCreate}
            disabled={isCreating}
            className="btn-primary"
          >
            {isCreating ? 'Создание...' : 'Создать программу'}
          </button>
        </div>
      </div>
    );
  };

  // Progress indicator
  const steps: { key: Step; label: string }[] = [
    { key: 'basic', label: 'Основное' },
    { key: 'workouts', label: 'Тренировки' },
    { key: 'exercises', label: 'Упражнения' },
    { key: 'review', label: 'Обзор' }
  ];

  const currentStepIndex = steps.findIndex(step => step.key === currentStep);

  return (
    <div className="program-creator bg-white border border-gray-200 rounded-lg p-6">
      {/* Progress Indicator */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <div key={step.key} className="flex items-center">
              <div className={`
                w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                ${index <= currentStepIndex 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-200 text-gray-600'
                }
              `}>
                {index + 1}
              </div>
              <span className={`ml-2 text-sm ${
                index <= currentStepIndex ? 'text-blue-600 font-medium' : 'text-gray-500'
              }`}>
                {step.label}
              </span>
              {index < steps.length - 1 && (
                <div className={`w-8 h-0.5 mx-4 ${
                  index < currentStepIndex ? 'bg-blue-500' : 'bg-gray-200'
                }`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step Content */}
      {currentStep === 'basic' && renderBasicStep()}
      {currentStep === 'workouts' && renderWorkoutsStep()}
      {currentStep === 'exercises' && renderExercisesStep()}
      {currentStep === 'review' && renderReviewStep()}
    </div>
  );
};

export default ProgramCreator;